<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { onMounted, ref, type Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { useToggle } from "~/core/composables/useToggle";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import { eventBus } from "~/core/services/eventBus";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import SectorDetailDtwins from "~/modules/sector/components/SectorDetailDtwins.vue";
import SectorDetailUserGroups from "~/modules/sector/components/SectorDetailUserGroups.vue";
import SectorDetailUsers from "~/modules/sector/components/SectorDetailUsers.vue";
import {
  MAX_SECTOR_DEPTH,
  PATH_SECTORS,
  PATH_SECTORS_CODE,
  PATH_SECTORS_CODE_DTWINS,
  PATH_SECTORS_CODE_GROUPS,
  PATH_SECTORS_CODE_SECTORS,
  PATH_SECTORS_CODE_USERS,
} from "~/modules/sector/constants";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SectorEvent } from "~/modules/sector/models/SectorEvent.interface";
import sectorsService from "~/modules/sector/services/sectors.service";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { findSectorInSectorTree } from "~/modules/sector/utils/findSectorInSectorTree";
import { findSectorParentInSectorTree } from "~/modules/sector/utils/findSectorParentInSectorTree";
import { getSectorDepth } from "~/modules/sector/utils/getSectorDepth";
import ILxcFolderPlus from "~icons/lxc/folder-plus";
import ILxcSettings from "~icons/lxc/settings";

const acl = useAcl();
const route = useRoute();
const router = useRouter();
const { can } = useAcl();
const { sectors } = storeToRefs(useSectorStore());

const [formDialogVisible, toggleFormDialogVisible] = useToggle();
const [removeDialogVisible, toggleRemoveDialogVisible] = useToggle();

const { t } = useI18n();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const sectorCode: ComputedRef<number> = computed(() =>
  Number(route.params.code),
);

const canUpdateSector = computed(() => {
  return acl.can(ACLRoles.CAN_WRITE_SECTOR) && sector.value?.statuses.granted;
});

const sectorDepth: ComputedRef<number> = computed(() => {
  return getSectorDepth(sectors.value ? [sectors.value] : [], sectorCode.value);
});

const isLoading = ref(false);
const error: Ref<LxcError | undefined> = ref();
const sector: Ref<Sector | undefined> = ref();
const parentSector: Ref<Sector | undefined> = ref();

const sectorToUpdate: Ref<Sector | undefined> = ref();
const sectorToUpdateParentCode: Ref<number | undefined> = ref();

const createSubSectorLabel: Ref<string> = ref(
  t("sectors.details.buttons.createSubSector"),
);

async function fetchSector() {
  isLoading.value = true;

  error.value = undefined;
  sector.value = undefined;
  parentSector.value = undefined;

  // Get sector from sector tree if present
  if (sectors.value) {
    sector.value = findSectorInSectorTree(sectors.value, sectorCode.value);
  }

  // Get sector from API if not present in sector tree
  if (!sector.value && currentOrganization.value?.code) {
    const response = await sectorsService.getSectorByCode(
      currentOrganization.value?.code,
      sectorCode.value,
    );

    if (LxcError.check(response)) {
      error.value = response;
    } else {
      sector.value = response;
    }
  }

  // Get parent sector from sector tree
  if (sectors.value && sector.value) {
    parentSector.value = findSectorParentInSectorTree(
      sectors.value,
      sector.value.code,
    );
  }

  isLoading.value = false;
}

//
// Navigation section. Logic / configuration responsible for the navigation.
//
const sectorsTabId = "sectors";
const dtwinsTabId = "dtwins";
const usersTabId = "users";
const groupsTabId = "groups";

const tabNavigations: ComputedRef<TabNavigation[]> = computed(() => {
  const tabNavigations = [];

  if (acl.can(ACLRoles.CAN_READ_SECTOR)) {
    tabNavigations.push({
      id: sectorsTabId,
      redirectPath: router.resolve({
        name: PATH_SECTORS_CODE_SECTORS,
        params: { code: sectorCode.value },
      }).href,
      labelTranslationKey: "sectors.details.tab.sectors.label",
      forceRefresh: true,
    });
  }

  if (sector.value?.statuses.granted) {
    if (acl.can(ACLRoles.CAN_READ_DEVICE)) {
      tabNavigations.push({
        id: dtwinsTabId,
        redirectPath: router.resolve({
          name: PATH_SECTORS_CODE_DTWINS,
          params: { code: sectorCode.value },
        }).href,
        labelTranslationKey: "sectors.details.tab.dtwins.label",
        forceRefresh: true,
      });
    }

    if (acl.can(ACLRoles.CAN_READ_USER)) {
      tabNavigations.push(
        {
          id: usersTabId,
          redirectPath: router.resolve({
            name: PATH_SECTORS_CODE_USERS,
            params: { code: sectorCode.value },
          }).href,
          labelTranslationKey: "sectors.details.tab.user.label",
          forceRefresh: true,
        },
        {
          id: groupsTabId,
          redirectPath: router.resolve({
            name: PATH_SECTORS_CODE_GROUPS,
            params: { code: sectorCode.value },
          }).href,
          labelTranslationKey: "sectors.details.tab.userGroup.label",
          forceRefresh: true,
        },
      );
    }
  }

  return tabNavigations;
});

function onSectorRemove() {
  fetchSector();
}

const onCreateSector = () => {
  if (sectorToUpdate.value) {
    sectorToUpdate.value = undefined;
  }
  sectorToUpdateParentCode.value = sector.value?.code;
  toggleFormDialogVisible();
};

const onUpdateSector = () => {
  if (sectors.value) {
    sectorToUpdateParentCode.value = findSectorParentInSectorTree(
      sectors.value,
      sectorCode.value,
    )?.code;
  }
  sectorToUpdate.value = sector.value;
  toggleFormDialogVisible();
};

const onDeleteSector = () => {
  toggleRemoveDialogVisible();
};

function onCloseSectorDelete(isSectorRemoved?: boolean) {
  toggleRemoveDialogVisible(false);
  if (isSectorRemoved) {
    router.push({ name: PATH_SECTORS });
  }
}

const onCloseSectorForm = async (refresh: boolean = false) => {
  toggleFormDialogVisible(false);
  if (refresh) {
    await fetchSector();
  }
};

async function onSectorHandler(event: SectorEvent) {
  if (
    event?.parentSectorCode === sectorCode.value ||
    event?.sectorCode === sectorCode.value
  ) {
    await fetchSector();
  }
}

const selectedTabIndex: Ref<number | undefined> = ref();
const sectorDetailDtwins: Ref<InstanceType<typeof SectorDetailDtwins> | null> =
  ref(null);
const sectorDetailUsers: Ref<InstanceType<typeof SectorDetailUsers> | null> =
  ref(null);

const sectorDetailGroups: Ref<InstanceType<
  typeof SectorDetailUserGroups
> | null> = ref(null);
const isDtwinsTabSelected = computed(
  () =>
    selectedTabIndex.value ===
    tabNavigations.value.findIndex(
      (tabNavigation) => tabNavigation.id === dtwinsTabId,
    ),
);
const isUsersTabSelected = computed(
  () =>
    selectedTabIndex.value ===
    tabNavigations.value.findIndex(
      (tabNavigation) => tabNavigation.id === usersTabId,
    ),
);
const isGroupsTabSelected = computed(
  () =>
    selectedTabIndex.value ===
    tabNavigations.value.findIndex(
      (tabNavigation) => tabNavigation.id === groupsTabId,
    ),
);
const isSectorsTabSelected = computed(
  () =>
    selectedTabIndex.value ===
    tabNavigations.value.findIndex(
      (tabNavigation) => tabNavigation.id === sectorsTabId,
    ),
);

const showDtwinAdd: ComputedRef<boolean> = computed((): boolean => {
  return isDtwinsTabSelected.value && can(ACLRoles.CAN_WRITE_DEVICE);
});

const showUserAdd: ComputedRef<boolean> = computed((): boolean => {
  return isUsersTabSelected.value && can(ACLRoles.CAN_WRITE_USER);
});

const showGroupAdd: ComputedRef<boolean> = computed((): boolean => {
  return isGroupsTabSelected.value && can(ACLRoles.CAN_WRITE_USER);
});

const showSectorAdd: ComputedRef<boolean> = computed((): boolean => {
  return (
    isSectorsTabSelected.value &&
    can(ACLRoles.CAN_WRITE_SECTOR) &&
    sectorDepth.value < MAX_SECTOR_DEPTH &&
    !!sector.value?.statuses.granted
  );
});

function onAddDtwin() {
  sectorDetailDtwins.value?.refresh();
}

function onAddUser() {
  sectorDetailUsers.value?.refresh();
}

function onAddGroup() {
  sectorDetailGroups.value?.refresh();
}

//
// Lifecycle Hooks. See: https://vuejs.org/api/composition-api-lifecycle
//
onMounted(() => {
  fetchSector();
  eventBus.on("sector", onSectorHandler);
});

watch(
  () => sectorCode.value,
  async () => {
    await fetchSector();
  },
);

onBeforeUnmount(() => {
  eventBus.off("sector", onSectorHandler);
});
</script>

<template>
  <page-component
    :breadcrumbs="[
      {
        title: parentSector?.name,
        href: parentSector
          ? router.resolve({
              name: PATH_SECTORS_CODE,
              params: { code: parentSector.code },
            }).href
          : undefined,
      },
      { title: sector?.name },
    ]"
    breadcrumbs-use-href
    :is-loading="isLoading"
  >
    <template #subtitle>
      <lxc-button
        v-if="!isLoading"
        :title="t('sectors.details.buttons.details')"
        type="borderless"
        class="!px-0 !py-0 ml-4"
        :icon="ILxcSettings"
        icon-class="!w-6 h-6"
        @click="onUpdateSector"
      />
    </template>

    <template #top-right>
      <div class="flex items-center gap-4">
        <lxc-button
          v-if="showSectorAdd"
          :title="createSubSectorLabel"
          type="primary"
          html-type="button"
          :icon="ILxcFolderPlus"
          @click="onCreateSector"
        >
          {{ createSubSectorLabel }}
        </lxc-button>
        <sector-toolbar-dtwin
          v-if="showDtwinAdd && sector"
          :sector="sector"
          @change="onAddDtwin"
        />
        <sector-toolbar-users
          v-if="showUserAdd && sector"
          :sector="sector"
          @change="onAddUser"
        />
        <sector-toolbar-user-groups
          v-if="showGroupAdd && sector"
          :sector="sector"
          @change="onAddGroup"
        />
        <sector-actions-dropdown
          v-if="canUpdateSector"
          :is-root-sector="sector?.isRootSector ?? false"
          :sector-depth="sectorDepth"
          @create="onCreateSector"
          @update="onUpdateSector"
          @remove="onDeleteSector"
        />
      </div>
    </template>
    <template v-if="sector" #default>
      <tabs-with-navigation
        :key="sectorCode"
        :tab-navigations="tabNavigations"
        @update:selected-tab-index="
          (newSelectedTabIndex: number) =>
            (selectedTabIndex = newSelectedTabIndex)
        "
      >
        <template #[sectorsTabId]>
          <sector-detail-sectors
            :sector="sector"
            :is-loading="isLoading"
            :error="error?.toError() || null"
            @remove="onSectorRemove"
          />
        </template>
        <template #[dtwinsTabId]>
          <sector-detail-dtwins ref="sectorDetailDtwins" :sector="sector" />
        </template>
        <template #[usersTabId]>
          <sector-detail-users ref="sectorDetailUsers" :sector="sector" />
        </template>
        <template #[groupsTabId]>
          <sector-detail-user-groups
            ref="sectorDetailGroups"
            :sector="sector"
          />
        </template>
      </tabs-with-navigation>
    </template>
  </page-component>

  <sector-form
    v-model:show="formDialogVisible"
    :parent-sector-code="sectorToUpdateParentCode"
    :sector="sectorToUpdate"
    @close="onCloseSectorForm(false)"
    @change="onCloseSectorForm(true)"
  />

  <sector-remove
    :is-visible="removeDialogVisible"
    :sector="sector"
    @close="onCloseSectorDelete()"
    @change="onCloseSectorDelete(true)"
  />
</template>
